import React, { createContext, useContext } from "react"
import { useQuery } from "@apollo/client/react"
import query from "./current_user.graphql"

export interface CurrentUser {
  id?: string
  slug?: string
  /** Feature keys the user has joined the waitlist for (FeatureRail "Get notified"). */
  waitlist?: string[]
  [key: string]: unknown
}

export interface CurrentProfile {
  id?: string
  slug?: string
  [key: string]: unknown
}

export const CurrentUserContext = createContext<CurrentUser>({})
export const CurrentProfileContext = createContext({})

export { query }

export interface Session {
  /** The signed-in user, or null when logged out. */
  currentUser: CurrentUser | null
  /** The current profile which right now is the current user's default profile when logged in . */
  currentProfile: CurrentProfile | null
  /** True when there is a real signed-in user (i.e. one with an id). */
  loggedIn: boolean
}

/**
 * Read the current session: the signed-in user plus a normalized `loggedIn` flag.
 *
 * Prefer this over reading CurrentUserContext directly. The context's default/empty value is `{}`,
 * so ad-hoc `!!currentUser` checks report "logged in" even when nobody is — `loggedIn` keys off a
 * real id instead, and `currentUser` is normalized to null when logged out.
 *
 *   const { currentUser, loggedIn } = useSession()
 */
export const useSession = (): Session => {
  const currentUser = useContext(CurrentUserContext) as
    | CurrentUser
    | null
    | undefined
  const currentProfile = useContext(CurrentProfileContext) as
    | CurrentProfile
    | null
    | undefined
  const loggedIn = Boolean(currentUser?.id)

  return {
    currentUser: loggedIn ? (currentUser as CurrentUser) : null,
    currentProfile: loggedIn ? (currentProfile as CurrentProfile) : null,
    loggedIn,
  }
}

export const SessionProvider = ({ children, currentProfile, currentUser }) => {
  const { data, loading, error } = useQuery(query)
  let currentUserData = data?.currentUser || currentUser
  let currentProfileData = currentUserData?.profile || currentProfile || {}
  return (
    <CurrentUserContext.Provider value={currentUserData}>
      <CurrentProfileContext.Provider value={currentProfileData}>
        {children}
      </CurrentProfileContext.Provider>
    </CurrentUserContext.Provider>
  )
}

export default SessionProvider
